import React, { useState, Component } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { X, ChevronDown } from 'lucide-react';
import { MyceliumNetworkHero } from '../components/MyceliumNetworkHero';
import { DNAHelixHero } from '../components/DNAHelixHero';
interface TeamMember {
  name: string;
  role: string;
  story: string;
  background: string;
  image: string;
}
interface AdvisorMember {
  name: string;
  role: string;
  bio: string;
  image?: string;
}
interface CommitteeMember {
  name: string;
  bio: string;
  image?: string;
}
export function TeamSplitScreen() {
  const [selectedMember, setSelectedMember] = useState<{
    type: 'core' | 'advisor' | 'committee';
    index: number;
  } | null>(null);
  const {
    scrollYProgress
  } = useScroll();
  const coreTeam: TeamMember[] = [{
    name: 'Natalya Wallin',
    role: 'GP & Managing Director',
    story: "I am a high conviction leader. I see opportunity in chaos and go after seemingly intransigent challenges. That is encoded in my DNA - my grandparents were shipped to Siberian labor camps and came to the United States as refugees to start over. So I come by my relentless drive and endurance honestly. I spent 10 years building and deploying a $75M global fund in the face of political headwinds. I successfully completed the Moab 240-miler (the brutal race that former Navy SEAL David Goggins DNF'd on his first attempt).",
    background: '10+ years experience fundraising, organization building, and fund management. Raised $75M from family offices, foundations, and governments to address systemic drivers of forced labor risks in target sectors.',
    image: 'https://cdn.magicpatterns.com/uploads/nPrbWRd99BLA7hL4gLSpQo/7_Natalya.jpg'
  }, {
    name: 'Patrick Livingston',
    role: 'Built Environment Expert & Portfolio Construction',
    story: "I solve high-stakes engineering and business challenges, from delivering multimillion-dollar infrastructure projects to scaling high-growth teams. I don't walk away from tough problems - I have spent my entire career at the same firm, seeing projects through from inception to construction, even when they were politically sensitive or on impossible timelines. I have designed infrastructure to withstand hurricanes, rebuilt critical systems in the wake of climate disasters.",
    background: '12+ years of experience in engineering, infrastructure, and business leadership. Delivered more than $1 billion in constructed infrastructure, solving complex technical, regulatory, and financial challenges to bring large-scale projects from concept to completion.',
    image: 'https://cdn.magicpatterns.com/uploads/oagLnQyesCH4gCMgV853L2/Pat_Headshot.jpg'
  }, {
    name: 'Rollin Nash',
    role: 'Operations & Resource Planning Specialist',
    story: 'I have spent most of my professional career in professions and sectors where my decisions were quite literally life and death. I excel in high stress, ambiguous situations for two reasons: First, I have an innate ability to stay calm under pressure, see the big picture, and direct efforts accordingly. Second, I create organizations that are ruthlessly efficient. Everyone knows what to do, how it fits into the bigger picture, why it matters, and when to make decisions.',
    background: '12+ years of experience in project management, operations, strategic planning, finance & back office. Planned, resourced, and implemented several multi-million dollar strategic initiatives including First-in-Human clinical trials, proof-of-concept military operations, and organizational transformations.',
    image: 'https://cdn.magicpatterns.com/uploads/aQWr6mTKqjSUTCytDF8GyV/Headshot.jpg'
  }];
  const advisors: AdvisorMember[] = [{
    name: 'Melissa Kim',
    role: 'Advisor & Original Co-founder',
    bio: 'Melissa Kim is an experienced executive, entrepreneur, and Vitality co-founder with deep expertise in leading teams to drive product innovation, business growth, and peak performance. Her focus is advising executives and startups, anticipating obstacles to growth, and proactively addressing business risks and points of potential failure across strategy, operations, and executive teams.',
    image: 'https://cdn.magicpatterns.com/uploads/7TaUSrmKqXQKcWYT8kcifC/Melissa_Headshot.jpg'
  }, {
    name: 'David Balkcom',
    role: 'Advisor, Finance & Agriculture',
    bio: 'David Balkcom is Managing Director at FTI and an experienced advisor to key stakeholders regarding start-ups, merger integration, turnaround and transformation, operational due diligence, business plan reviews, and synergy assessments. He has concentrated industry experience in controlled environment agriculture, consumer durables, and natural resources/commodities.',
    image: 'https://cdn.magicpatterns.com/uploads/8EqyD625fqmGoK7SfdyZho/David_Headshot.jpg'
  }, {
    name: 'Dr. Jean Baderschneider',
    role: 'Advisor, Global Engagement & Climate Finance',
    bio: 'Dr. Baderschneider brings 35 years of extensive international experience in supply chain management and logistics, procurement, and capital projects along with a deep understanding of high-risk operations. She is the non-executive Director of Fortescue Metals Group and former VP of Global Procurement at ExxonMobil. She serves on The McCain Institute Board, is a member of the Board of the Nizami Ganjavi International Center and the International Advisory Committee of COP29.',
    image: 'https://cdn.magicpatterns.com/uploads/oZoevN8jFFQA4NHrwEzrwg/Jean_Bio_pic_2026.png'
  }, {
    name: 'Matt Esposito',
    role: 'Advisor, Investor Relations',
    bio: 'Strategic advisor with deep expertise in venture capital and startup ecosystems.',
    image: 'https://cdn.magicpatterns.com/uploads/hYDVwGv8avLAL5fqGmTYmq/Matt_Headshot.jpg'
  }];
  const committee: CommitteeMember[] = [{
    name: 'Jordan McAlister',
    bio: 'Head of Investment & Innovation Strategy with Hyperspace Ventures',
    image: 'https://cdn.magicpatterns.com/uploads/sUj2TqUKbbrXFrhPY4ndmT/Jordan_Headshot.jpg'
  }, {
    name: 'Kate Knight',
    bio: 'Executive scaling coach and advisor with 20 years of experience',
    image: 'https://cdn.magicpatterns.com/uploads/ua9ycBvhPbjjrzg8hWCre9/Kate_Headshot.jpg'
  }, {
    name: 'Robin Berry',
    bio: 'Director of Lab to Market Innovation at ASU Biomimicry Center',
    image: 'https://cdn.magicpatterns.com/uploads/srnieMTZyCgwgg24unbedV/Robin_Headshot.jpg'
  }, {
    name: 'Olivia Hanrahan-Soar',
    bio: 'Expert in innovative finance and impact investing',
    image: 'https://cdn.magicpatterns.com/uploads/3V4HKj9gszSL8YVnqQCohF/8_Olivia.jpg'
  }, {
    name: 'Medina Korda Poole',
    bio: 'High-impact leader managing $150M+ portfolios globally',
    image: 'https://cdn.magicpatterns.com/uploads/ir1uoqUiVwJ7RcLbkkq7CA/6_Medina.png'
  }, {
    name: 'Chris Burton',
    bio: 'Experienced investor and operator with a focus on sustainable technologies.',
    image: 'https://cdn.magicpatterns.com/uploads/pRjZWzihcQPZV74t433sRU/5_Chris.jpg'
  }];
  return <div className="min-h-screen bg-black" data-id="element-1962">
      {/* Progress Indicator */}
      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-vitalityTeal z-50 origin-left" style={{
      scaleX: scrollYProgress
    }} data-id="element-1963" />

      {/* Hero Intro */}
      <section className="h-screen flex items-center justify-center relative overflow-hidden" data-id="element-1964">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0e2533] to-black" data-id="element-1965" />

        {/* Background image - mycelium network microscope */}
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1576086213369-97a306d36557?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-15" data-id="element-1966" />

        {/* Emphasized SVG overlay */}
        <div className="absolute inset-0 opacity-60" data-id="element-1967">
          <MyceliumNetworkHero data-id="element-1968" />
        </div>

        <div className="relative z-10 text-center px-4" data-id="element-1969">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-6xl md:text-8xl font-bold text-white mb-6" data-id="element-1970">
            Our Team
          </motion.h1>
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.5
        }} className="flex justify-center" data-id="element-1971">
            <ChevronDown className="w-8 h-8 text-vitalityTeal animate-bounce" data-id="element-1972" />
          </motion.div>
        </div>
      </section>

      {/* Core Team - Full Viewport Sections */}
      {coreTeam.map((member, index) => <SplitScreenSection key={member.name} member={member} index={index} imageOnLeft={index % 2 === 0} onReadMore={() => setSelectedMember({
      type: 'core',
      index
    })} data-id="element-1973" />)}

      {/* Advisors - Compact Section */}
      <section className="min-h-screen flex items-center bg-gradient-to-b from-black to-[#0e2533] py-20" data-id="element-1974">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-1975">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-16" data-id="element-1976">
            <h2 className="text-5xl font-bold text-white mb-4" data-id="element-1977">Advisors</h2>
            <p className="text-gray-400 text-xl" data-id="element-1978">
              Strategic guidance from industry veterans
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-1979">
            {advisors.map((advisor, index) => <motion.div key={advisor.name} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2
          }} className="p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm" data-id="element-1980">
                <div className="flex items-start gap-4 sm:gap-6" data-id="element-1981">
                  {advisor.image ? <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex-shrink-0 overflow-hidden border-2 border-[#a0e6e9]/30" data-id="element-1982">
                      <img src={advisor.image} alt={`${advisor.name} headshot`} className="w-full h-full object-cover" data-id="element-1983" />
                    </div> : <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-[#a0e6e9]/20 to-[#a0e6e9]/5 flex items-center justify-center flex-shrink-0 border-2 border-[#a0e6e9]/30" data-id="element-1984">
                      <span className="text-2xl sm:text-3xl font-bold text-[#a0e6e9]/70" data-id="element-1985">
                        {advisor.name[0]}
                      </span>
                    </div>}
                  <div data-id="element-1986">
                    <h3 className="text-xl sm:text-2xl font-bold text-white mb-2" data-id="element-1987">
                      {advisor.name}
                    </h3>
                    <p className="text-[#a0e6e9] text-sm font-medium mb-3" data-id="element-1988">
                      {advisor.role}
                    </p>
                    <p className="text-gray-400 text-sm sm:text-base" data-id="element-1989">
                      {advisor.bio}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Committee - Simple List */}
      <section className="py-20 bg-[#0e2533]" data-id="element-1990">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1991">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-12" data-id="element-1992">
            <h2 className="text-4xl font-bold text-white mb-4" data-id="element-1993">
              Investment Review Committee
            </h2>
            <p className="text-gray-500 text-lg mb-2" data-id="element-1994">
              Expert evaluation and due diligence
            </p>
            <p className="text-gray-400 text-sm italic" data-id="element-1995">
              These experts contribute their insights on a voluntary basis in
              support of the mission
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1996">
            {committee.map((member, index) => <motion.div key={member.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="p-6 rounded-xl bg-white/5 border border-white/10" data-id="element-1997">
                <div className="flex items-start gap-4" data-id="element-1998">
                  {member.image ? <div className="w-12 h-12 rounded-full flex-shrink-0 overflow-hidden border-2 border-white/10" data-id="element-1999">
                      <img src={member.image} alt={`${member.name} headshot`} className="w-full h-full object-cover" data-id="element-2000" />
                    </div> : <div className="w-12 h-12 rounded-full bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center flex-shrink-0 border-2 border-white/10" data-id="element-2001">
                      <span className="text-lg font-bold text-white/50" data-id="element-2002">
                        {member.name[0]}
                      </span>
                    </div>}
                  <div data-id="element-2003">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-2004">
                      {member.name}
                    </h3>
                    <p className="text-gray-500 text-sm" data-id="element-2005">{member.bio}</p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence data-id="element-2006">
        {selectedMember !== null && <motion.div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setSelectedMember(null)} data-id="element-2007">
            <motion.div className="bg-[#1c3f4d] rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-y-auto border-2 border-vitalityTeal/30 relative" initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} onClick={e => e.stopPropagation()} data-id="element-2008">
              <button onClick={() => setSelectedMember(null)} className="absolute top-6 right-6 text-white hover:text-vitalityTeal transition-colors" data-id="element-2009">
                <X className="w-6 h-6" data-id="element-2010" />
              </button>
              <div className="p-10" data-id="element-2011">
                <p className="text-vitalityTeal text-sm font-bold tracking-wider uppercase mb-2" data-id="element-2012">
                  {coreTeam[selectedMember.index].role}
                </p>
                <h3 className="text-4xl font-bold text-white mb-8" data-id="element-2013">
                  {coreTeam[selectedMember.index].name}
                </h3>
                <div className="space-y-6" data-id="element-2014">
                  <div data-id="element-2015">
                    <h4 className="text-white font-bold text-xl mb-3" data-id="element-2016">Story</h4>
                    <p className="text-gray-300 leading-relaxed text-lg" data-id="element-2017">
                      {coreTeam[selectedMember.index].story}
                    </p>
                  </div>
                  <div data-id="element-2018">
                    <h4 className="text-white font-bold text-xl mb-3" data-id="element-2019">
                      Background
                    </h4>
                    <p className="text-gray-300 leading-relaxed text-lg" data-id="element-2020">
                      {coreTeam[selectedMember.index].background}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// Split Screen Section Component
function SplitScreenSection({
  member,
  index,
  imageOnLeft,
  onReadMore
}: {
  member: TeamMember;
  index: number;
  imageOnLeft: boolean;
  onReadMore: () => void;
}) {
  const {
    scrollYProgress
  } = useScroll();
  const imageY = useTransform(scrollYProgress, [index * 0.25, (index + 1) * 0.25], ['20%', '-20%']);
  return <section className="min-h-screen flex items-center relative overflow-hidden bg-black" data-id="element-2021">
      <div className="w-full" data-id="element-2022">
        <div className="grid lg:grid-cols-2 lg:min-h-screen" data-id="element-2023">
          {/* Image Side */}
          <motion.div className={`relative min-h-[50vh] lg:min-h-0 ${imageOnLeft ? 'lg:order-1' : 'lg:order-2'}`} initial={{
          opacity: 0,
          x: imageOnLeft ? -50 : 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-2024">
            <motion.div className="absolute inset-0" style={{
            y: imageY
          }} data-id="element-2025">
              <div className="absolute inset-0 bg-gradient-to-b lg:bg-gradient-to-r from-black/50 to-transparent z-10" data-id="element-2026" />
              <img src={member.image} alt={member.name} className="w-full h-full object-cover" style={{
              objectPosition: 'center 20%'
            }} data-id="element-2027" />
            </motion.div>
          </motion.div>

          {/* Content Side */}
          <motion.div className={`flex items-center px-6 sm:px-8 md:px-16 py-12 lg:py-20 ${imageOnLeft ? 'lg:order-2' : 'lg:order-1'}`} initial={{
          opacity: 0,
          x: imageOnLeft ? 50 : -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-2028">
            <div data-id="element-2029">
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.4
            }} className="text-vitalityTeal text-sm font-bold tracking-wider uppercase mb-4" data-id="element-2030">
                {member.role}
              </motion.p>

              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.5
            }} className="text-5xl md:text-6xl font-bold text-white mb-8" data-id="element-2031">
                {member.name}
              </motion.h2>

              <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.6
            }} className="text-gray-300 text-lg md:text-xl leading-relaxed mb-8" data-id="element-2032">
                {member.story}
              </motion.p>

              <motion.button initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.7
            }} onClick={onReadMore} className="inline-flex items-center gap-2 px-6 py-3 min-h-[44px] bg-vitalityTeal/10 hover:bg-vitalityTeal/20 border border-vitalityTeal/30 rounded-lg text-vitalityTeal font-semibold transition-colors" data-id="element-2033">
                Read Full Background
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}